import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';
import { motion, AnimatePresence } from 'motion/react';

import {
  CreditProfileAuthCustomerInput,
  CreditProfileAuthStatusProfile,
  TuCreditReportIdentityProfile,
} from '@generated-gql/generated-types';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';
import { useEmitRewardableEvent } from '@lib/hooks/nerdwallet-plus';
import { RewardableEventType } from '@lib/types/nerdwallet-plus-rewards';
import { useAnalytics } from '@lib/segment';
import InitialStep from './steps/initial';
import BasicInfoStep from './steps/basic-info';
import ShortSSNStep from './steps/short-ssn';
import FullSSNStep from './steps/full-ssn';
import OtpConfirmationStep from './steps/otp-confirmation';
import OtpPasscodeEntryStep from './steps/otp-passcode-entry';
import VerificationQuestions from './steps/verification-questions';
import BlockedStep from './steps/blocked';
import styles from './tu-modal-stepper.module.scss';

import {
  getSegmentTuAuthenticationSuccessEvent,
  hasOtpAuthenticationQuestions,
} from '@components/transunion-flow/utils';

export type TuErrors = {
  isRateLimited?: boolean;
  duplicateEmail?: string;
};

interface StepperContextType {
  authStatusProfile?: CreditProfileAuthStatusProfile;
  setAuthStatusProfile: (profile: CreditProfileAuthStatusProfile) => void;
  tosAcknowledged: boolean;
  setTosAcknowledged: (acknowledged: boolean) => void;
  tuCustomerInfo?: CreditProfileAuthCustomerInput;
  setTuCustomerInfo: (info?: CreditProfileAuthCustomerInput) => void;
  tuErrors: TuErrors;
  setTuErrors: (errors: TuErrors) => void;
}

export enum TuStep {
  // Step 1 - Fetch initial user information and create trust session
  TU_INITIAL = 'TU_INITIAL',
  // Step 2 - Main user input form
  TU_BASIC_INFO = 'TU_BASIC_INFO',
  // Step 3 - 4 digit SSN screen with Terms of Agreement
  TU_SHORT_SSN = 'TU_SHORT_SSN',
  // Step 3a - If user information is not found, have user provide full SSN
  TU_FULL_SSN = 'TU_FULL_SSN',
  // Step 4 - If user qualifies for OTP verification, have them specify how to receive passcode
  TU_OTP_CONFIRMATION = 'TU_OTP_CONFIRMATION',
  // Step 4a - Screen for inputting passcode or requesting a new one
  TU_OTP_PASSCODE_ENTRY = 'TU_OTP_PASSCODE_ENTRY',
  // Step 4b - If user does not qualify for OTP and has KBA questions to answer
  TU_VERIFICATION_QUESTIONS = 'TU_VERIFICATION_QUESTIONS',
  // Step 5 - User successfully authenticated with TU and was able to pull credit report
  TU_SUCCESS = 'TU_SUCCESS',
  // Step 5a - Not enough credit history or user failed to authenticate with TU
  TU_BLOCKED = 'TU_BLOCKED',
  // Step 6 - After blocked state, to terminate the flow
  TU_END = 'TU_END',
}

export const StepperContext = createContext<StepperContextType | undefined>(
  undefined,
);

export const StepperProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [authStatusProfile, setAuthStatusProfile] =
    useState<CreditProfileAuthStatusProfile | null>();
  const [tosAcknowledged, setTosAcknowledged] = useState<boolean>(false);
  const [tuCustomerInfo, setTuCustomerInfo] =
    useState<CreditProfileAuthCustomerInput | null>();
  const [tuErrors, setTuErrors] = useState<TuErrors>({});

  return (
    <StepperContext.Provider
      value={{
        authStatusProfile,
        setAuthStatusProfile,
        tosAcknowledged,
        setTosAcknowledged,
        tuCustomerInfo,
        setTuCustomerInfo,
        tuErrors,
        setTuErrors,
      }}
    >
      {children}
    </StepperContext.Provider>
  );
};

export const useTuStepper = () => {
  const context = useContext(StepperContext);

  if (!context) {
    throw new Error('useTuStepper must be used within a StepperProvider');
  }
  return context;
};

export const TuModalStepper = () => {
  const {
    tuVisibility: { tuStep },
    setTuVisibility,
  } = useTuVisibilityContext();
  const { setAuthStatusProfile } = useTuStepper();
  const { segmentTrack } = useAnalytics();
  const [emitReward] = useEmitRewardableEvent();

  // Callback for when the initializeCreditProfile mutation succeeds
  const handleCreateTuCustomerSuccess = useCallback(
    (data: CreditProfileAuthStatusProfile) => {
      setAuthStatusProfile(data);
      const hasOtp = hasOtpAuthenticationQuestions(data);
      if (data.questions && !hasOtp) {
        // User did not qualify for OTP, fall back to KBA questions
        setTuVisibility({
          show: true,
          tuStep: TuStep.TU_VERIFICATION_QUESTIONS,
        });
      }
      if (hasOtp) {
        // Move on to the screen where a user can choose how to verify with OTP
        setTuVisibility({
          show: true,
          tuStep: TuStep.TU_OTP_CONFIRMATION,
        });
      }
    },
    [setAuthStatusProfile, setTuVisibility],
  );

  // Callback for when a user has successfully authenticated and enrolled with TU
  const handleTuActivationSuccess = useCallback(
    async (data: TuCreditReportIdentityProfile) => {
      // Trigger segment event when success
      const { name, props } = getSegmentTuAuthenticationSuccessEvent(data);
      await segmentTrack(name, props);
      emitReward(RewardableEventType.ACTIVATED_TU);
      setTuVisibility({
        show: true,
        tuStep: TuStep.TU_SUCCESS,
      });
    },
    [segmentTrack, emitReward, setTuVisibility],
  );

  const renderStep = useCallback(() => {
    switch (tuStep) {
      case TuStep.TU_INITIAL:
        return <InitialStep />;
      case TuStep.TU_BASIC_INFO:
        return <BasicInfoStep />;
      case TuStep.TU_SHORT_SSN:
        return (
          <ShortSSNStep onInitializeSuccess={handleCreateTuCustomerSuccess} />
        );
      case TuStep.TU_FULL_SSN:
        return (
          <FullSSNStep onInitializeSuccess={handleCreateTuCustomerSuccess} />
        );
      case TuStep.TU_OTP_CONFIRMATION:
        return <OtpConfirmationStep />;
      case TuStep.TU_OTP_PASSCODE_ENTRY:
        return (
          <OtpPasscodeEntryStep
            onActivationSuccess={handleTuActivationSuccess}
          />
        );
      case TuStep.TU_VERIFICATION_QUESTIONS:
        return (
          <VerificationQuestions
            onActivationSuccess={handleTuActivationSuccess}
          />
        );
      case TuStep.TU_BLOCKED:
        return <BlockedStep onActivationSuccess={handleTuActivationSuccess} />;
    }
  }, [handleCreateTuCustomerSuccess, handleTuActivationSuccess, tuStep]);

  const isInitialStep = tuStep === TuStep.TU_INITIAL;

  return (
    <div className={styles.modalContainer}>
      <AnimatePresence initial={false}>
        <motion.div
          key={tuStep}
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={
            isInitialStep ? { x: 0, opacity: 0 } : { x: '-100%', opacity: 0 }
          }
          style={{ minWidth: '100%' }}
          transition={{
            opacity: { duration: isInitialStep ? 0 : 0.25 },
            x: { duration: 0.25 },
          }}
        >
          {renderStep()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const TuModalStepperWithProvider = () => (
  <StepperProvider>
    <TuModalStepper />
  </StepperProvider>
);

export default TuModalStepperWithProvider;
